import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Layers, AlertTriangle, User } from 'lucide-react'
import { Card, Button, Input } from '@brix/ui'
import { supabase } from '@/infrastructure/supabase/client'
import { useAuthStore } from '@/store/authStore'
import { usePermissions } from '@/hooks/usePermissions'

// ─── Rename workspace ─────────────────────────────────────────────────────────

function RenameForm() {
  const { tenant, setTenant, userRole } = useAuthStore()
  const [name, setName] = useState(tenant?.name ?? '')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)

  const handleSave = async () => {
    if (!tenant || !name.trim() || name.trim() === tenant.name) return
    setSaving(true)
    setError(null)
    const { error: err } = await supabase
      .from('tenants')
      .update({ name: name.trim() })
      .eq('id', tenant.id)
    setSaving(false)
    if (err) { setError(err.message); return }
    setTenant({ ...tenant, name: name.trim() }, userRole!)
    setSaved(true)
    setTimeout(() => setSaved(false), 2000)
  }

  return (
    <div className="space-y-3">
      <Input
        label="Workspace name"
        value={name}
        onChange={e => { setName(e.target.value); setSaved(false) }}
        onKeyDown={e => e.key === 'Enter' && void handleSave()}
      />
      {error && <p className="text-xs text-red-600">{error}</p>}
      <Button
        onClick={() => void handleSave()}
        loading={saving}
        disabled={!name.trim() || name.trim() === tenant?.name}
        variant="secondary"
        className="w-full sm:w-auto"
      >
        {saved ? '✓ Saved' : 'Save name'}
      </Button>
    </div>
  )
}

// ─── Delete workspace ─────────────────────────────────────────────────────────

function DeleteWorkspace() {
  const { tenant, signOut } = useAuthStore()
  const navigate = useNavigate()
  const [confirming, setConfirming] = useState(false)
  const [input, setInput] = useState('')
  const [deleting, setDeleting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleDelete = async () => {
    if (!tenant) return
    setDeleting(true)
    setError(null)
    const { error: err } = await supabase.rpc('delete_own_tenant')
    if (err) {
      setDeleting(false)
      setError(err.message)
      return
    }
    await signOut()
    navigate('/login')
  }

  if (!confirming) {
    return (
      <button
        onClick={() => setConfirming(true)}
        className="rounded-lg border border-red-200 px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 transition-colors cursor-pointer"
      >
        Delete workspace
      </button>
    )
  }

  return (
    <div className="rounded-xl border border-red-200 bg-red-50 p-5 space-y-4">
      <div className="flex items-start gap-3">
        <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-red-600" />
        <div>
          <p className="text-sm font-semibold text-red-900">This will permanently delete:</p>
          <ul className="mt-1 list-disc list-inside text-sm text-red-700 space-y-0.5">
            <li>All sites and pages</li>
            <li>All blog posts and media</li>
            <li>All team members and invites</li>
            <li>Billing data and API keys</li>
          </ul>
        </div>
      </div>
      <div>
        <p className="mb-2 text-sm text-red-800">
          Type <strong>{tenant?.slug}</strong> to confirm:
        </p>
        <input
          aria-label={`Type ${tenant?.slug} to confirm`}
          value={input}
          onChange={e => setInput(e.target.value)}
          placeholder={tenant?.slug}
          className="w-full rounded-lg border border-red-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
        />
      </div>
      {error && <p className="text-xs text-red-700">{error}</p>}
      <div className="flex gap-2">
        <button
          onClick={() => { setConfirming(false); setInput('') }}
          className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors"
        >
          Cancel
        </button>
        <button
          disabled={input !== tenant?.slug || deleting}
          onClick={() => void handleDelete()}
          className="flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {deleting && <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white border-t-transparent" />}
          Delete workspace permanently
        </button>
      </div>
    </div>
  )
}

// ─── Delete personal account ───────────────────────────────────────────────────

function DeleteAccount() {
  const { user, signOut } = useAuthStore()
  const navigate = useNavigate()
  const [confirming, setConfirming] = useState(false)
  const [input, setInput] = useState('')
  const [deleting, setDeleting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleDelete = async () => {
    setDeleting(true)
    setError(null)
    const { data, error: err } = await supabase.functions.invoke('delete-account')
    if (err || (data as { error?: string } | null)?.error) {
      setDeleting(false)
      setError((data as { error?: string } | null)?.error ?? err?.message ?? 'Could not delete your account')
      return
    }
    await signOut()
    navigate('/login')
  }

  if (!confirming) {
    return (
      <button
        onClick={() => setConfirming(true)}
        className="rounded-lg border border-red-200 px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 transition-colors cursor-pointer"
      >
        Delete my account
      </button>
    )
  }

  return (
    <div className="rounded-xl border border-red-200 bg-red-50 p-5 space-y-4">
      <div className="flex items-start gap-3">
        <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-red-600" />
        <div>
          <p className="text-sm font-semibold text-red-900">This will permanently delete your personal account.</p>
          <p className="mt-1 text-sm text-red-700">
            If you're the sole member of your workspace, the workspace and all its data are deleted too.
            If you own a workspace with other members, you'll need to remove them or delete the workspace first.
          </p>
        </div>
      </div>
      <div>
        <p className="mb-2 text-sm text-red-800">
          Type <strong>{user?.email}</strong> to confirm:
        </p>
        <input
          aria-label={`Type ${user?.email} to confirm`}
          value={input}
          onChange={e => setInput(e.target.value)}
          placeholder={user?.email ?? ''}
          className="w-full rounded-lg border border-red-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
        />
      </div>
      {error && <p className="text-xs text-red-700">{error}</p>}
      <div className="flex gap-2">
        <button
          onClick={() => { setConfirming(false); setInput(''); setError(null) }}
          className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors"
        >
          Cancel
        </button>
        <button
          disabled={input !== user?.email || deleting}
          onClick={() => void handleDelete()}
          className="flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {deleting && <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white border-t-transparent" />}
          Delete my account permanently
        </button>
      </div>
    </div>
  )
}

// ─── Email change ─────────────────────────────────────────────────────────────

function EmailSection() {
  const { user } = useAuthStore()
  const [editing, setEditing] = useState(false)
  const [newEmail, setNewEmail] = useState('')
  const [status, setStatus] = useState<'idle' | 'loading' | 'sent' | 'error'>('idle')
  const [errorMsg, setErrorMsg] = useState('')

  const handleSend = async () => {
    const trimmed = newEmail.trim()
    if (!trimmed || trimmed === user?.email) return
    setStatus('loading')
    const { error } = await supabase.auth.updateUser({ email: trimmed })
    if (error) {
      setStatus('error')
      setErrorMsg(error.message)
    } else {
      setStatus('sent')
    }
  }

  const cancel = () => { setEditing(false); setNewEmail(''); setStatus('idle') }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-medium text-gray-500">Email</p>
          <p className="text-sm text-gray-900">{user?.email}</p>
        </div>
        {!editing && status !== 'sent' && (
          <button
            onClick={() => setEditing(true)}
            className="text-xs font-medium text-violet-600 hover:text-violet-800 transition-colors"
          >
            Change
          </button>
        )}
      </div>

      {editing && status !== 'sent' && (
        <div className="space-y-2 pt-1">
          <Input
            label="New email address"
            type="email"
            value={newEmail}
            onChange={e => { setNewEmail(e.target.value); setStatus('idle') }}
            placeholder={user?.email ?? ''}
            autoComplete="email"
          />
          {status === 'error' && <p className="text-xs text-red-600">{errorMsg}</p>}
          <div className="flex gap-2">
            <Button variant="secondary" onClick={cancel} className="flex-1 sm:flex-none">
              Cancel
            </Button>
            <Button
              loading={status === 'loading'}
              disabled={!newEmail.trim() || newEmail.trim() === user?.email}
              onClick={() => void handleSend()}
              className="flex-1 sm:flex-none"
            >
              Send confirmation
            </Button>
          </div>
        </div>
      )}

      {status === 'sent' && (
        <p className="text-xs text-green-600">
          Confirmation sent to <strong>{newEmail.trim()}</strong> — check your inbox to confirm.
        </p>
      )}
    </div>
  )
}

// ─── Personal account ─────────────────────────────────────────────────────────

function AccountForm() {
  const { user } = useAuthStore()
  const [displayName, setDisplayName] = useState(
    (user?.user_metadata?.['full_name'] as string | undefined) ?? ''
  )
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSave = async () => {
    setError(null)
    if (password && password !== confirm) { setError("Passwords don't match"); return }
    if (password && password.length < 8) { setError('Password must be at least 8 characters'); return }
    setSaving(true)
    const updates: Record<string, unknown> = {}
    if (displayName.trim()) updates['data'] = { full_name: displayName.trim() }
    if (password) updates['password'] = password
    if (Object.keys(updates).length === 0) { setSaving(false); return }
    const { error: err } = await supabase.auth.updateUser(updates as Parameters<typeof supabase.auth.updateUser>[0])
    setSaving(false)
    if (err) { setError(err.message); return }
    setPassword(''); setConfirm('')
    setSaved(true)
    setTimeout(() => setSaved(false), 2000)
  }

  return (
    <div className="space-y-4">
      <Input
        label="Display name"
        value={displayName}
        onChange={e => { setDisplayName(e.target.value); setSaved(false) }}
        placeholder="Your name"
      />
      <div className="border-t border-gray-100 pt-4 space-y-3">
        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Change password</p>
        <Input
          label="New password"
          type="password"
          autoComplete="new-password"
          value={password}
          onChange={e => { setPassword(e.target.value); setSaved(false) }}
          placeholder="Leave blank to keep current"
        />
        {password && (
          <Input
            label="Confirm new password"
            type="password"
            autoComplete="new-password"
            value={confirm}
            onChange={e => setConfirm(e.target.value)}
          />
        )}
      </div>
      {error && <p className="text-xs text-red-600">{error}</p>}
      <div className="flex items-center gap-3">
        <Button
          onClick={() => void handleSave()}
          loading={saving}
          disabled={!displayName.trim() && !password}
          variant="secondary"
          className="w-full sm:w-auto"
        >
          {saved ? '✓ Saved' : 'Save account'}
        </Button>
        {saved && <span className="text-xs text-green-600">Saved</span>}
      </div>
    </div>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function SettingsPage() {
  const { tenant, userRole } = useAuthStore()
  const perms = usePermissions()

  return (
    <div className="p-8 max-w-2xl">
      <h1 className="mb-6 text-2xl font-bold text-gray-900">Settings</h1>

      <Card padding="md" className="mb-4">
        <div className="flex items-center gap-2 mb-4">
          <User className="h-4 w-4 text-gray-400" />
          <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Your account</h2>
        </div>
        <EmailSection />
        <div className="my-4 border-t border-gray-100" />
        <AccountForm />
      </Card>

      <Card padding="md" className="mb-4">
        <h2 className="mb-4 text-sm font-semibold text-gray-700 uppercase tracking-wide">Workspace</h2>
        <div className="flex items-center gap-3 mb-4">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-violet-100">
            <Layers className="h-5 w-5 text-violet-600" />
          </div>
          <div>
            <p className="font-semibold text-gray-900">{tenant?.name}</p>
            <p className="text-xs text-gray-500">{tenant?.slug}.brix.app</p>
          </div>
        </div>

        <div className="space-y-2 mb-4">
          <div className="flex items-center justify-between rounded-lg bg-gray-50 px-4 py-3 text-sm">
            <span className="text-gray-500">Plan</span>
            <span className="font-medium capitalize text-gray-900">{tenant?.plan}</span>
          </div>
          <div className="flex items-center justify-between rounded-lg bg-gray-50 px-4 py-3 text-sm">
            <span className="text-gray-500">Your role</span>
            <span className="font-medium capitalize text-gray-900">{userRole}</span>
          </div>
        </div>

        {perms.canAccessSettings && <RenameForm />}
      </Card>

      <Card padding="md">
        <h2 className="mb-4 text-sm font-semibold text-gray-700 uppercase tracking-wide">Danger zone</h2>
        <div className="space-y-3">
          {perms.canDeleteWorkspace && <DeleteWorkspace />}
          <DeleteAccount />
        </div>
      </Card>
    </div>
  )
}
